import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { useUiStore } from "@/stores/ui";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useProjectsStore } from "@/stores/projects";
import { useSwipe } from "@/hooks/use-swipe";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FindReplaceModal } from "@/components/editor/FindReplaceModal";
import { useValidatorsStore } from "@/stores/validators";

// Workspace hooks
import { useFileOperations } from "@/hooks/workspace/useFileOperations";
import {
  copyTextToClipboard,
} from "@/hooks/workspace/useContextBuilder";
import { useWorkspaceStore } from "@/stores/workspace";
import { useMobileWorkspace } from "@/hooks/workspace/useMobileWorkspace";
import { useShallow } from 'zustand/react/shallow';
import { getLanguageFromPath } from '@/stores/workspace';
import { useInstructionsStore } from '@/stores/instructions';

// Workspace components
import { CodeEditor } from "./workspace/CodeEditor";
import { ContextBuilder } from "./workspace/ContextBuilder";
import { FileExplorer } from "./workspace/explorer/FileExplorer";
import { WorkspaceCommandPalette } from "@/components/workspace/WorkspaceCommandPalette";
import { ProjectSwitcher } from "./workspace/ProjectSwitcher";
import { useFileTree } from '@/hooks/workspace/useFileTree';
import { PasteToTaskButton } from '@/components/PasteToTaskButton';

export default function WorkspacePage() {
  // Centralized Zustand store for workspace state and actions
  const {
    openFilePath,
  } = useWorkspaceStore(useShallow((state) => {
    return {
      openFilePath: state.openFilePath,
    }
  }));


  // Additional state
  const [isFindReplaceOpen, setIsFindReplaceOpen] = useState(false);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [generatedContext, setGeneratedContext] = useState<string | null>(null);
  const [isContextDialogOpen, setIsContextDialogOpen] = useState(false);
  const [isDialogCopying, setIsDialogCopying] = useState(false);

  // Hooks
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
  const { results: validatorResults } = useValidatorsStore();
  const {
 byId: projectsById,
    loadProjects,
 activeProjectId,
  } = useProjectsStore(useShallow(state => ({ byId: state.byId, loadProjects: state.loadProjects, activeProjectId: state.activeProjectId })));
  const activeProject = useMemo(() => activeProjectId ? projectsById[activeProjectId] : null, [activeProjectId, projectsById]);

  const fileOps = useFileOperations();
  const fileTreeLoader = useFileTree();
  const { mobileView, setMobileView } = useMobileWorkspace();
  const { isFileExplorerSearchVisible, isGitPanelVisible } = useUiStore(
    useShallow((state) => ({
      isFileExplorerSearchVisible: state.isFileExplorerSearchVisible,
      isGitPanelVisible: state.isGitPanelVisible,
    }))
  );

  const onRefreshFileTree = useCallback(() => {
    toast.promise(fileTreeLoader.loadFileTree("."), {
      loading: "Refreshing file tree...",
      success: "File tree refreshed.",
      error: (err) => `Failed to refresh file tree: ${err.message}`,
    });
  }, [fileTreeLoader]);


  const handleCopyGeneratedContext = useCallback(async () => {
    if (!generatedContext || isDialogCopying) return;
    setIsDialogCopying(true);
    try {
      await copyTextToClipboard(generatedContext);
      toast.success("Context copied to clipboard.");
    } catch (error: any) {
      toast.error("Copy failed", { description: error.message });
    } finally {
      setIsDialogCopying(false);
    }
  }, [generatedContext, isDialogCopying]);


  // Initialize
  useEffect(() => {
    setHeaderTitle("Workspace");
    loadProjects();
  }, [setHeaderTitle, loadProjects]);

  useEffect(() => {
    setGeneratedContext(null);
    setIsContextDialogOpen(false);
    setIsDialogCopying(false);
  }, [activeProjectId]);

  // Swipe gestures for mobile
  const swipeHandlers = useSwipe({
    onSwipeLeft: () => {
      if (isMobile && mobileView === "files" && openFilePath) {
        setMobileView("content");
      }
    },
    onSwipeRight: () => {
      if (isMobile && mobileView === "content") {
        setMobileView("files");
      }
    },
  });

  // Editor actions

  const handleFindNext = (query: string) => {
    editorRef.current
      ?.getModel()
      ?.findNextMatch(
        query,
        editorRef.current.getPosition()!,
        false,
        true,
        null,
        true
      );
  };
  const handleReplace = (find: string, replace: string) => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    const selection = editor?.getSelection();
    if (!editor || !model || !selection) return;

    if (model.getValueInRange(selection) === find) {
      editor.executeEdits("replace-action", [
        { range: selection, text: replace },
      ]);
    }
    handleFindNext(find);
  };
  const handleReplaceAll = (find: string, replace: string) => {
    const editor = editorRef.current;
    const model = editor?.getModel();
    if (!editor || !model) return;

    const matches = model.findMatches(find, true, false, true, null, true);
    const edits = matches.map((match) => ({
      range: match.range,
      text: replace,
    }));
    editor.executeEdits("replace-all-action", edits);
  };

  // Test failures for open file
  const testFailuresForOpenFile = useMemo(() => {
    if (!validatorResults.latest?.tests || !openFilePath) return undefined;
    const fileResult = validatorResults.latest.tests.byFile.find(
      (f) => f.path === openFilePath
    );

    if (fileResult && fileResult.status === "failed") {
      return fileResult.failures
        .map((failure: any) => {
          const line = failure.details?.crash?.lineno;
          const message = failure.error;
          if (line && message) return { line, message };
          return null;
        })
        .filter(Boolean) as { line: number; message: string }[];
    }
    return undefined;
  }, [validatorResults, openFilePath]);

  if (!activeProject) {
    return (
      <div className="flex items-center justify-center h-full text-center text-muted-foreground">
        <div>
          <p>No active project selected.</p>
          <Button variant="link" onClick={() => navigate("/pages/projects/")}>
            Go to Projects
          </Button>
        </div>
      </div>
    );
  }

  const fileCache = fileOps.cachedContents?.[openFilePath || ""];
  const coverage = fileCache?.coverage;
  const coverageDesynced = fileCache?.coverageDesynced;

  return (
    <div
      key={activeProjectId}
      className="h-full flex flex-col overflow-hidden max-w-[100vw]"
      {...swipeHandlers}
    >
      {/* Modals */}
      <FindReplaceModal
        isOpen={isFindReplaceOpen}
        onClose={() => setIsFindReplaceOpen(false)}
        onFindNext={handleFindNext}
        onReplace={handleReplace}
        onReplaceAll={handleReplaceAll}
      />

      <WorkspaceCommandPalette
      />

      <Dialog
        open={isContextDialogOpen}
        onOpenChange={(open) => {
          setIsContextDialogOpen(open);
          if (!open) {
            setIsDialogCopying(false);
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Generated Context</DialogTitle>
            <DialogDescription>
              Review the automatically generated context and copy it when
              you&apos;re ready.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            readOnly
            value={generatedContext ?? ""}
            className="min-h-[300px] font-mono text-xs"
          />
          <DialogFooter>
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                onClick={handleCopyGeneratedContext}
                disabled={isDialogCopying || !generatedContext}
              >
                {isDialogCopying ? "Copying..." : "Copy to Clipboard"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setIsContextDialogOpen(false)}
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="flex items-center justify-between p-2 border-b shrink-0">
        <ProjectSwitcher />
        <div>
          <PasteToTaskButton />
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 p-1 gap-1 flex flex-col overflow-hidden">
        {isMobile ? (
          <div className="flex-1 flex flex-col gap-1 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-hidden">
              {mobileView === "files" ? (
                <FileExplorer
                  onRefresh={onRefreshFileTree}
                />
              ) : (
                <CodeEditor
                  coverage={coverage}
                  testFailures={testFailuresForOpenFile}
                  coverageDesynced={coverageDesynced}
                />
              )}
            </div>
            <div className="shrink-0 p-1 border rounded flex flex-col gap-1">
              <div className="flex gap-1">
                <Button
                  className="flex-1 h-7 px-2 py-0 text-xs"
                  variant={mobileView === "files" ? "secondary" : "ghost"}
                  onClick={() => setMobileView("files")}
                >
                  Files
                </Button>
                <Button
                  className="flex-1 h-7 px-2 py-0 text-xs"
                  variant={mobileView === "content" ? "secondary" : "ghost"}
                  onClick={() => setMobileView("content")}
                  disabled={!openFilePath}
                >
                  Code
                </Button>
              </div>
              {mobileView === "files" && 
               !isFileExplorerSearchVisible && 
               !isGitPanelVisible && (
              <ContextBuilder
              />)}
            </div>
          </div>
        ) : (
          <ResizablePanelGroup
            direction="horizontal"
            className="flex-1"
            autoSaveId="sprocket-workspace-layout-horizontal"
          >
            <ResizablePanel defaultSize={30} minSize={20}>
              <ResizablePanelGroup
                direction="vertical"
                className="flex-1"
                autoSaveId="sprocket-workspace-layout-vertical"
              >
                <ResizablePanel defaultSize={60} minSize={20}>
                  <FileExplorer
                    onRefresh={onRefreshFileTree}
                  />
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={40} minSize={20}>
                  <div className="h-full flex flex-col">
                    <ContextBuilder />
                    
                  </div>
                  
                </ResizablePanel>
              </ResizablePanelGroup>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={70}>
              <CodeEditor
                coverage={coverage}
                testFailures={testFailuresForOpenFile}
                coverageDesynced={coverageDesynced}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
    </div>
  );
}
